import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { argbFromHex, hexFromArgb, themeFromSourceColor } from '@material/material-color-utilities';
import { ITenantTheme, CurrentTenantService } from '@suvo-bi-users';

@Injectable({ providedIn: 'root' })
export class ThemingService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly tenantService = inject(CurrentTenantService);

  init() {
    const { theme } = this.tenantService.currentTenant();
    if (isPlatformBrowser(this.platformId) && theme) {
      this.applyTheme(theme);
    }
  }

  applyTheme(themeConfig: ITenantTheme) {
    if (themeConfig?.primaryColor) this.applyPalette('primary', themeConfig.primaryColor);
    if (themeConfig?.secondaryColor) this.applyPalette('secondary', themeConfig.secondaryColor);
    if (themeConfig?.tertiaryColor) this.applyPalette('tertiary', themeConfig.tertiaryColor);

    this.applyContrast('primary', themeConfig.primaryContrast || 'light');
    this.applyContrast('secondary', themeConfig.secondaryContrast || 'light');
    this.applyContrast('tertiary', themeConfig.tertiaryContrast || 'light');

    if (themeConfig.primaryBackgroundColor) {
      document.body.style.setProperty('--primary-background', themeConfig.primaryBackgroundColor);
    }
  }

  protected applyPalette(paletteName: 'primary' | 'secondary' | 'tertiary', hexValue: string) {
    const palette = themeFromSourceColor(argbFromHex(hexValue)).palettes.primary;

    document.body.style.setProperty(`--colour-${paletteName}`, hexValue);
    document.body.style.setProperty(`--colour-${paletteName}-500`, hexValue);

    document.body.style.setProperty(`--colour-${paletteName}-50`, hexFromArgb(palette.tone(90)));
    document.body.style.setProperty(`--colour-${paletteName}-100`, hexFromArgb(palette.tone(75)));
    document.body.style.setProperty(`--colour-${paletteName}-200`, hexFromArgb(palette.tone(62.5)));
    document.body.style.setProperty(`--colour-${paletteName}-300`, hexFromArgb(palette.tone(44)));
    document.body.style.setProperty(`--colour-${paletteName}-400`, hexFromArgb(palette.tone(30)));
    document.body.style.setProperty(`--colour-${paletteName}-600`, hexFromArgb(palette.tone(15)));
    document.body.style.setProperty(`--colour-${paletteName}-700`, hexFromArgb(palette.tone(13)));
    document.body.style.setProperty(`--colour-${paletteName}-800`, hexFromArgb(palette.tone(12)));
    document.body.style.setProperty(`--colour-${paletteName}-900`, hexFromArgb(palette.tone(10)));

    // TODO: more saturated variants
    // A100, A200, A400, A700
  }

  protected applyContrast(
    paletteName: 'primary' | 'secondary' | 'tertiary',
    contrast: 'light' | 'dark',
  ) {
    const contrastColour = contrast === 'light' ? '#fff' : '#000';
    const apply = (hueCode: number) =>
      document.body.style.setProperty(
        `--colour-${paletteName}-contrast-${hueCode}`,
        contrastColour,
      );

    document.body.style.setProperty(`--colour-${paletteName}-contrast`, contrastColour);

    // 50, 100, 200 - always very light; no need to change

    // change - 300, 400, 500, 600, 700
    for (let i = 300; i < 800; i += 100) {
      apply(i);
    }

    // 800, 900 - always very dark; no need to change

    // // TODO: uncomment when dynamic A100 - A700 hues are implemented
    // apply('A100');
    // apply('A200');
    // apply('A400');
    // apply('A700');
  }
}
